import { AxiosResponse, isAxiosError } from 'axios';
import { z } from 'zod';

import { client } from '../Client';
import {
    BoxType,
    GroupType,
    type PaginatedScanTypes,
    ScanType,
    ScanTypeDetails,
    ScanTypeDetailsSchema,
    ScanTypesSchema,
    ScanTypeStatus
} from './schemas';

const defaultResponseHandler =
    <T extends z.ZodTypeAny>(schema: T, errorMessage: string) =>
    ({ data }: AxiosResponse): z.infer<T> => {
        const result = schema.safeParse(data);

        if (!result.success) throw new Error(errorMessage);

        return result.data;
    };

const ErrorResponseSchema = z.object({
    message: z.string()
});

const defaultErrorHandler = (error: unknown) => {
    if (!isAxiosError(error)) throw error;

    const result = ErrorResponseSchema.safeParse(error.response?.data);

    if (!result.success) throw error;

    throw new Error(result.data.message);
};

export type ScanTypesParams = Partial<{
    location: readonly string[] | string;
    name: string;
    allowFailedChecks: boolean;
    groupType: readonly GroupType[];
    boxType: readonly BoxType[];
    isProtected: boolean;
    status: readonly string[];
    page: number;
    show: number;
}>;

type GetScanTypesOptions = {
    params?: ScanTypesParams;
    signal?: AbortSignal;
};

type GetScanTypeDetailsOptions = {
    signal?: AbortSignal;
};

export type PatchScanTypePayload = Partial<{
    name: string;
    description: string;
    groupType: GroupType;
    boxType: BoxType;
    locations: readonly string[];
    isProtected: boolean;
    allowFailedChecks: boolean;
    status: ScanTypeStatus;
}>;

type ScanTypesService = {
    getScanTypes: (options: GetScanTypesOptions) => Promise<PaginatedScanTypes>;
    getAllScanTypesMerged: (options: GetScanTypesOptions, accumulatedData: ScanType[]) => Promise<PaginatedScanTypes>;
    getScanType: (id: number, options: GetScanTypeDetailsOptions) => Promise<ScanTypeDetails>;
    patchScanType: (id: number, data: PatchScanTypePayload) => Promise<ScanTypeDetails>;
};

const getScanTypes = ({ params, signal }: GetScanTypesOptions) =>
    client
        .get('/scans/types', { params, signal })
        .then(defaultResponseHandler(ScanTypesSchema, 'Failed to parse Scan Types, please try again'))
        .catch(defaultErrorHandler);

const getScanType = (id: number, { signal }: GetScanTypeDetailsOptions) =>
    client
        .get(`/scans/types/${id}`, { signal })
        .then(defaultResponseHandler(ScanTypeDetailsSchema, 'Failed to parse Scan Type, please try again'))
        .catch(defaultErrorHandler);

const patchScanType = (id: number, data: PatchScanTypePayload) =>
    client
        .patch(`/scans/types/${id}`, data)
        .then(defaultResponseHandler(ScanTypeDetailsSchema, 'Failed to parse Scan Type, please try again'))
        .catch(defaultErrorHandler);

const getAllScanTypesMerged = async (
    { params, signal }: GetScanTypesOptions,
    accumulatedData: ScanType[] = []
): Promise<PaginatedScanTypes> => {
    const currentPage = params?.page ?? 1;
    const updatedParams = { ...params, page: currentPage };

    const response: PaginatedScanTypes = await scanTypesService.getScanTypes({
        params: updatedParams,
        signal
    });

    const mergedData = accumulatedData.concat(response.items);

    const totalPages = Math.ceil(response.count / response.show);
    if (currentPage < totalPages) {
        return getAllScanTypesMerged({ params: { ...params, page: currentPage + 1 }, signal }, mergedData);
    }

    return { ...response, items: mergedData };
};

export const scanTypesService = {
    getScanTypes,
    getScanType,
    patchScanType,
    getAllScanTypesMerged
} satisfies ScanTypesService;
